import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "@/components/Buttons.jsx";
import EmailEditorModal from "@/components/EmailEditorModal.jsx";
import SubscriptionDot from "@/pages/merchants/subscriptionDot.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import CRMAccessBlocker from "@/partials/CRMAccessBlocker.jsx";
import {
  loadCrmSettings,
  saveCrmSettings,
  updateDomains,
  addDomain as addDomainAction,
  removeDomain as removeDomainAction,
  updateField,
  setHeaderEditorOpen,
  setInstructionsModalOpen,
  setWarningModalOpen,
  setSelectedDomain,
  resetToOriginal,
} from "@/store/crmSettings/action.js";
import { addToast } from "@/store/app/action.js";

function CrmSettings() {
  const dispatch = useDispatch();
  const [newDomain, setNewDomain] = useState("");

  // Get CRM settings from Redux store
  const {
    domains,
    defaultFromAddress,
    defaultFromName,
    defaultReplyToAddress,
    defaultEmailHeader,
    emailHeaderDesign,
    development_mode,
    loading,
    saving,
    dirty,
    showHeaderEditor,
    showInstructionsModal,
    showWarningModal,
    selectedDomain,
    originalDomains,
  } = useSelector((state) => state.crmSettings);

  // Get selected merchant from app state
  const { selectedMerchant } = useSelector((state) => state.app);

  // Load CRM settings on component mount
  useEffect(() => {
    dispatch(loadCrmSettings());
  }, [dispatch]);

  // No need for checkDirty function anymore - Redux handles dirty state

  const submit = async (e) => {
    e.preventDefault();

    // Check if any domains were removed (any status) - only if not using saucey email
    if (!development_mode) {
      const removedDomains = originalDomains.filter((originalDomain) => {
        const isRemoved = !domains.some(
          (currentDomain) =>
            currentDomain.domain_name === originalDomain.domain_name
        );
        return isRemoved;
      });

      if (removedDomains.length > 0) {
        dispatch(setWarningModalOpen(true));
        return;
      }
    }

    await performSubmit();
  };

  const performSubmit = async () => {
    try {
      // Prepare domains payload - only send domain_name for new domains, and only if not using saucey email
      const domainsPayload = development_mode
        ? []
        : domains.map((domain) => ({
            domain_name: domain.domain_name,
          }));

      await dispatch(
        saveCrmSettings({
          domains: domainsPayload,
          default_from_address: development_mode
            ? `merchant+${selectedMerchant?.id}@sdengage.com`
            : defaultFromAddress,
          default_from_name: defaultFromName,
          default_reply_to_address: development_mode
            ? "no-reply@sdengage.com"
            : defaultReplyToAddress,
          default_header_html: defaultEmailHeader,
          default_header_design: emailHeaderDesign,
          development_mode: development_mode,
        })
      );

      dispatch(setWarningModalOpen(false));
    } catch (error) {
      // Error handling is done in the Redux action
    }
  };

  // Domain management functions
  const addDomain = () => {
    if (
      !development_mode &&
      newDomain.trim() &&
      !domains.some((d) => d.domain_name === newDomain.trim())
    ) {
      dispatch(addDomainAction(newDomain.trim()));
      setNewDomain("");
    }
  };

  const removeDomain = (domainToRemove) => {
    if (!development_mode) {
      dispatch(removeDomainAction(domainToRemove));
    }
  };

  const handleDomainKeyPress = (e) => {
    if (e.key === "Enter" && !development_mode) {
      e.preventDefault();
      addDomain();
    }
  };

  const showInstructions = (domain) => {
    dispatch(setSelectedDomain(domain));
    dispatch(setInstructionsModalOpen(true));
  };

  const handleHeaderSave = (data) => {
    dispatch(updateField("defaultEmailHeader", data.html));
    dispatch(updateField("emailHeaderDesign", data.design));
    dispatch(setHeaderEditorOpen(false));
  };

  if (loading) {
    return (
      <div className="grow">
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Loading CRM settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grow">
      <form onSubmit={submit}>
        {/* Panel body */}
        <div className="p-6 space-y-6">
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
            CRM Settings
          </h2>

          {/* Email Settings Section */}
          <section>
            <h3 className="text-lg text-slate-800 dark:text-slate-100 font-semibold mb-4">
              Email Settings
            </h3>

            <div className="space-y-6">
              {/* Use Saucey Email Checkbox */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="development_mode"
                  className="form-checkbox w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  checked={development_mode}
                  onChange={(e) =>
                    dispatch(updateField("development_mode", e.target.checked))
                  }
                />
                <label
                  htmlFor="development_mode"
                  className="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Development Mode
                </label>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                When enabled, you will only be able to send test emails through Saucey's email service
                with predefined addresses
              </p>

              {/* Email Domains - Only show when not using saucey email */}
              {!development_mode ? (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email Domains
                  </label>
                  <div className="space-y-3">
                    {/* Add new domain */}
                    <div className="flex gap-2">
                      <input
                        className="form-input flex-1"
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyPress={handleDomainKeyPress}
                        placeholder="e.g., yourcompany.com"
                        disabled={development_mode}
                      />
                      <PrimaryButton
                        type="button"
                        onClick={addDomain}
                        disabled={
                          development_mode ||
                          !newDomain.trim() ||
                          domains.some(
                            (d) => d.domain_name === newDomain.trim()
                          )
                        }
                      >
                        Add
                      </PrimaryButton>
                    </div>

                    {/* Domain list */}
                    {domains.length > 0 && (
                      <div className="space-y-2">
                        {domains.map((domain, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-theme-surface-accent rounded border border-slate-200 dark:border-slate-700"
                          >
                            <div className="flex items-center gap-2">
                              <SubscriptionDot
                                subscription={{ status: domain.status }}
                              />
                              <span className="text-slate-700 dark:text-slate-200">
                                {domain.domain_name}
                              </span>
                              {domain.status === "new" && (
                                <span className="text-xs text-slate-400">
                                  (New - not submitted yet)
                                </span>
                              )}
                              {domain.status === "pending" && (
                                <span className="text-xs text-slate-500">
                                  (Verification pending)
                                </span>
                              )}
                              {domain.status === "failed" && (
                                <span className="text-xs text-red-500">
                                  (Verification failed)
                                </span>
                              )}
                              {domain.status === "verified" && (
                                <span className="text-xs text-green-500">
                                  (Verified)
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {domain.status === "pending" && (
                                <SecondaryButton
                                  type="button"
                                  onClick={() => showInstructions(domain)}
                                >
                                  Show Instructions
                                </SecondaryButton>
                              )}
                              <DangerButton
                                type="button"
                                onClick={() => removeDomain(domain.domain_name)}
                              >
                                Remove
                              </DangerButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {domains.length === 0 && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No domains added yet. Add at least one domain for
                        sending emails.
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    The domains that will be used for sending emails
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                        Development Mode
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        When using development mode, you can only send test
                        emails to addresses associated with your account. It can
                        be any user accounts that's listed under your merchant
                        or billing emails.
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                        Development mode is useful for testing your email
                        designs while you get your own domain setup.
                      </p>

                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                        It is strongly encouraged to add a domain to your
                        account. This will allow you to send emails from your
                        own domain to all your customers and enable all Customer
                        Engagement features.
                      </p>
                      <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1 mt-2">
                        <p>
                          <strong>From Address:</strong> merchant+
                          {selectedMerchant?.id || "..."}@sdengage.com
                        </p>
                        <p>
                          <strong>Reply To:</strong> no-reply@sdengage.com
                        </p>
                      </div>
                    </div>
                    
                  </div>
                </div>
              )}

              {/* Default From Address */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="default_from_address"
                >
                  Default From Address
                  {development_mode && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Locked
                    </span>
                  )}
                </label>
                {development_mode ? (
                  <div className="form-input w-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 cursor-not-allowed border-slate-300 dark:border-slate-600">
                    {selectedMerchant?.id
                      ? `merchant+${selectedMerchant.id}@sdengage.com`
                      : "Loading..."}
                  </div>
                ) : (
                  <input
                    className="form-input w-full"
                    id="default_from_address"
                    name="default_from_address"
                    type="email"
                    value={defaultFromAddress}
                    onChange={(e) =>
                      dispatch(
                        updateField("defaultFromAddress", e.target.value)
                      )
                    }
                    placeholder="noreply@yourcompany.com"
                  />
                )}
                <p className="text-xs text-slate-500 mt-1">
                  {development_mode
                    ? "Predefined sender address when using Saucey email service"
                    : "Default sender address for all CRM emails"}
                </p>
              </div>

              {/* Default From Name */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="default_from_name"
                >
                  Default From Name
                </label>
                <input
                  className="form-input w-full"
                  id="default_from_name"
                  name="default_from_name"
                  type="text"
                  value={defaultFromName}
                  onChange={(e) =>
                    dispatch(updateField("defaultFromName", e.target.value))
                  }
                  placeholder="Your Company Name"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Default sender name for all CRM emails
                </p>
              </div>

              {/* Default Reply To Address */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="default_reply_to_address"
                >
                  Default Reply To Address
                  {development_mode && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Locked
                    </span>
                  )}
                </label>
                {development_mode ? (
                  <div className="form-input w-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 cursor-not-allowed border-slate-300 dark:border-slate-600">
                    no-reply@sdengage.com
                  </div>
                ) : (
                  <input
                    className="form-input w-full"
                    id="default_reply_to_address"
                    name="default_reply_to_address"
                    type="email"
                    value={defaultReplyToAddress}
                    onChange={(e) =>
                      dispatch(
                        updateField("defaultReplyToAddress", e.target.value)
                      )
                    }
                    placeholder="support@yourcompany.com"
                  />
                )}
                <p className="text-xs text-slate-500 mt-1">
                  {development_mode
                    ? "Predefined reply-to address when using Saucey email service"
                    : "Default reply-to address for all CRM emails"}
                </p>
              </div>

              {/* Default Email Header */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Default Email Header
                </label>
                <div className="border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900/20 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      This header will be automatically included in all
                      broadcast emails
                    </p>
                    <PrimaryButton
                      type="button"
                      onClick={() => dispatch(setHeaderEditorOpen(true))}
                    >
                      Edit Header
                    </PrimaryButton>
                  </div>
                  {defaultEmailHeader ? (
                    <div className="border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-theme-surface-accent p-3">
                      <div
                        className="text-xs text-slate-700 dark:text-slate-200"
                        dangerouslySetInnerHTML={{ __html: defaultEmailHeader }}
                      />
                    </div>
                  ) : (
                    <div className="border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-theme-surface-accent p-3">
                      <span className="text-slate-400 text-sm">
                        No default header set
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Save Button */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-200 dark:border-slate-700">
            <PrimaryButton
              type="submit"
              disabled={!dirty || saving}
              loading={saving}
            >
              Save Changes
            </PrimaryButton>
          </div>
        </div>
      </form>
      <EmailEditorModal
        isOpen={showHeaderEditor}
        onClose={() => dispatch(setHeaderEditorOpen(false))}
        onSave={handleHeaderSave}
        initialDesign={emailHeaderDesign}
        title="Edit Default Email Header"
        minHeight={600}
        modalSize="max-w-[1200px]"
        modalMinHeight="90vh"
      />

      {/* Domain Verification Instructions Modal */}
      <ModalBasic
        id="instructions-modal"
        modalOpen={showInstructionsModal}
        setModalOpen={(open) => dispatch(setInstructionsModalOpen(open))}
        title={`Domain Verification Instructions for ${selectedDomain?.domain_name}`}
      >
        {selectedDomain && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">
                DNS Records to Add
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Add the following DNS records to your domain to complete
                verification:
              </p>

              {/* DNS Records Table */}
              <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-700 px-4 py-3 border-b border-slate-200 dark:border-slate-600">
                  <div className="grid grid-cols-12 gap-4 text-sm font-medium text-slate-700 dark:text-slate-300">
                    <div className="col-span-2">Type</div>
                    <div className="col-span-5">Name</div>
                    <div className="col-span-5">Value</div>
                  </div>
                </div>
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {/* TXT Records */}
                  {selectedDomain.txt &&
                    Object.keys(selectedDomain.txt).length > 0 &&
                    Object.entries(selectedDomain.txt).map(([name, value]) => (
                      <div key={`txt-${name}`} className="px-4 py-3">
                        <div className="grid grid-cols-12 gap-4 text-sm">
                          <div className="col-span-2 flex items-center">
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              TXT
                            </span>
                          </div>
                          <div className="col-span-5 flex items-center justify-between">
                            <span className="font-mono text-slate-600 dark:text-slate-400 truncate">
                              {name}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(name);
                                dispatch(
                                  addToast({
                                    type: "success",
                                    message: "Name copied to clipboard",
                                    timer: 3,
                                  })
                                );
                              }}
                              className="ml-2 p-1 text-blue-600 hover:text-blue-700 transition-colors"
                              title="Copy name"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          </div>
                          <div className="col-span-5 flex items-center justify-between">
                            <span className="font-mono text-slate-600 dark:text-slate-400 truncate">
                              {value}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(value);
                                dispatch(
                                  addToast({
                                    type: "success",
                                    message: "Value copied to clipboard",
                                    timer: 3,
                                  })
                                );
                              }}
                              className="ml-2 p-1 text-blue-600 hover:text-blue-700 transition-colors"
                              title="Copy value"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* CNAME Records */}
                  {selectedDomain.cname &&
                    Object.keys(selectedDomain.cname).length > 0 &&
                    Object.entries(selectedDomain.cname).map(
                      ([name, value]) => (
                        <div key={`cname-${name}`} className="px-4 py-3">
                          <div className="grid grid-cols-12 gap-4 text-sm">
                            <div className="col-span-2 flex items-center">
                              <span className="font-medium text-slate-700 dark:text-slate-300">
                                CNAME
                              </span>
                            </div>
                            <div className="col-span-5 flex items-center justify-between">
                              <span className="font-mono text-slate-600 dark:text-slate-400 truncate">
                                {name}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(name);
                                  dispatch(
                                    addToast({
                                      type: "success",
                                      message: "Name copied to clipboard",
                                      timer: 3,
                                    })
                                  );
                                }}
                                className="ml-2 p-1 text-blue-600 hover:text-blue-700 transition-colors"
                                title="Copy name"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              </button>
                            </div>
                            <div className="col-span-5 flex items-center justify-between">
                              <span className="font-mono text-slate-600 dark:text-slate-400 truncate">
                                {value}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(value);
                                  dispatch(
                                    addToast({
                                      type: "success",
                                      message: "Value copied to clipboard",
                                      timer: 3,
                                    })
                                  );
                                }}
                                className="ml-2 p-1 text-blue-600 hover:text-blue-700 transition-colors"
                                title="Copy value"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-4 mt-4">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Instructions:
                </h4>
                <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                  <li>1. Log into your domain registrar or DNS provider</li>
                  <li>2. Navigate to the DNS management section</li>
                  <li>3. Add the TXT and CNAME records shown above</li>
                  <li>4. Wait for DNS propagation (can take up to 48 hours)</li>
                  <li>
                    5. The domain status will automatically update to "Verified"
                    once DNS records are detected
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </ModalBasic>

      {/* Domain Removal Warning Modal */}
      <ModalBasic
        id="warning-modal"
        modalOpen={showWarningModal}
        setModalOpen={(open) => dispatch(setWarningModalOpen(open))}
        title="Domain Removal Warning"
      >
        <div className="space-y-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 text-yellow-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                Removed Domains Will Need Re-verification
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                You have removed one or more domains from your settings. If you
                proceed, these domains will need to be re-verified before they
                can be used for sending emails.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> Re-verification requires adding DNS
                  records and waiting for propagation (up to 48 hours).
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <SecondaryButton
              onClick={() => {
                dispatch(setWarningModalOpen(false));
                // Restore original domains
                dispatch(resetToOriginal());
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={performSubmit} loading={saving}>
              Proceed Anyway
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
      <CRMAccessBlocker skipSettingsCheck />
    </div>
  );
}

export default CrmSettings;
